import { StanButton } from '@/components';
import { cn, createIconElement } from '@/lib';
import IcPlus from '@/assets/icons/ic-plus.svg';
import IcCheckCircle from '@/assets/icons/ic-check-circle.svg';
import IcStarFill from '@/assets/icons/ic-star-fill.svg';
import ArrowDashed from '@/assets/images/funnels/arrow-dashed.svg';
import emptyStateLeadCapture from '@/assets/images/funnels/empty-state-lead-capture.png';
import emptyStateMainProduct from '@/assets/images/funnels/empty-state-main-product.png';
import emptyStateUpsell from '@/assets/images/funnels/empty-state-upsell.png';

interface FunnelEmptyStateProps {
    onCreateFunnel: () => void;
    isLoading: boolean;
    isFunnelAllowed: boolean;
}

interface FunnelStageLegendProps {
    label: string;
    withCheck?: boolean;
}

const FunnelStageLegend = ({ label, withCheck = false }: FunnelStageLegendProps) => (
    <div className='flex items-center justify-center gap-1.5'>
        {withCheck && createIconElement(IcCheckCircle, { className: 'size-4 text-[#23A15D]' })}
        <span className='text-caption-1-regular text-dark'>{label}</span>
    </div>
);

interface FunnelPreviewCardProps {
    label: string;
    productTitle: string;
    imageSrc: string;
    imageAlt: string;
    badges: React.ReactNode;
}

const FunnelPreviewCard = ({ label, productTitle, imageSrc, imageAlt, badges }: FunnelPreviewCardProps) => (
    <li className='bg-neutral-subtle mx-auto flex w-full max-w-md flex-col items-center gap-6 overflow-hidden rounded-2xl pt-6 lg:mx-0 lg:w-60 lg:max-w-none xl:w-72'>
        <p className='text-heading-h3 text-dark'>{label}</p>
        <div className='bg-canvas flex w-[calc(100%-3rem)] flex-col items-center gap-3 rounded-t-md shadow-md xl:w-[calc(100%-6rem)]'>
            <div className='relative w-full'>
                <img src={imageSrc} alt={imageAlt} className='h-32 w-full rounded object-cover' />
                {badges}
            </div>
            <p className='text-label-md-bold text-dark tracking-wide uppercase'>{productTitle}</p>
            <div className='flex w-full flex-col items-start gap-1.5'>
                <span aria-hidden='true' className='bg-neutral-dimmed h-1 w-full rounded-full' />
                <span aria-hidden='true' className='bg-neutral-dimmed h-1 w-full rounded-full' />
                <span aria-hidden='true' className='bg-neutral-dimmed h-1 w-1/2 rounded-full' />
            </div>
        </div>
    </li>
);

interface DecorativeBadgeProps {
    children: React.ReactNode;
    className?: string;
}

const DecorativeBadge = ({ children, className }: DecorativeBadgeProps) => (
    <span
        className={cn(
            'text-caption-2-bold text-dark absolute flex items-center gap-1 rounded px-2.5 font-semibold whitespace-nowrap',
            className
        )}
    >
        {children}
    </span>
);

export const FunnelEmptyState = ({ onCreateFunnel, isLoading, isFunnelAllowed }: FunnelEmptyStateProps) => {
    if (!isFunnelAllowed) {
        return (
            <div className='mx-auto flex w-full max-w-screen-lg flex-col items-start gap-4 px-4 py-12 lg:px-2 lg:py-16'>
                <h2 className='text-heading-h2 text-dark'>Funnels</h2>
                <p className='text-body-md text-gray max-w-md'>
                    Funnels are not available on your current plan. Upgrade to access this feature.
                </p>
            </div>
        );
    }

    return (
        <div className='mx-auto flex w-full max-w-screen-lg flex-col items-start gap-10 pt-8 pb-20 md:px-4 lg:gap-12 lg:px-2 lg:pt-12'>
            <header className='flex flex-col gap-3 lg:gap-4'>
                <h1 className='text-heading-h1 text-dark lg:text-title-display-2 font-extrabold!'>
                    Sell more in one go with Funnels
                </h1>
                <p className='text-body-md text-dark'>
                    Market multiple products to your customer during a single checkout session, boosting sales by up to
                    20%
                </p>
            </header>

            <div className='flex w-full flex-col items-stretch gap-6 lg:w-auto lg:items-start lg:gap-4'>
                <ul className='grid grid-cols-1 items-stretch gap-12 lg:grid-cols-[repeat(3,15rem)] lg:gap-x-6 lg:gap-y-0 xl:grid-cols-[repeat(3,18rem)]'>
                    <FunnelPreviewCard
                        label='Entry Page'
                        productTitle='Film Preset'
                        imageSrc={emptyStateLeadCapture}
                        imageAlt='Entry page product thumbnail'
                        badges={
                            <DecorativeBadge className='bottom-2 -left-4 -rotate-6 bg-[#74FBE3]'>
                                *FREE*
                            </DecorativeBadge>
                        }
                    />
                    <FunnelPreviewCard
                        label='Main Product'
                        productTitle='Photo Course'
                        imageSrc={emptyStateMainProduct}
                        imageAlt='Main product thumbnail'
                        badges={
                            <>
                                <DecorativeBadge className='top-2 -right-4 rotate-6 bg-[#F9DE69]'>
                                    <IcStarFill className='size-3' />
                                    5.0
                                </DecorativeBadge>
                                <DecorativeBadge className='-bottom-2 -left-2 -rotate-3 bg-[#FECACA]'>
                                    20% OFF
                                </DecorativeBadge>
                            </>
                        }
                    />
                    <FunnelPreviewCard
                        label='Upsell'
                        productTitle='Preset Pack'
                        imageSrc={emptyStateUpsell}
                        imageAlt='Upsell product thumbnail'
                        badges={
                            <DecorativeBadge className='top-1/2 left-2/3 -translate-x-1/2 -translate-y-1/2 rotate-6 bg-[#74FBE3]'>
                                *SPECIAL OFFER*
                            </DecorativeBadge>
                        }
                    />
                </ul>

                <ul
                    className='relative hidden grid-cols-[repeat(3,15rem)] items-center gap-x-6 lg:grid xl:grid-cols-[repeat(3,18rem)]'
                    aria-label='Funnel flow legend'
                >
                    <li>
                        <FunnelStageLegend label='Freebie claimed' withCheck />
                    </li>
                    <li>
                        <FunnelStageLegend label='Queued paid product' />
                    </li>
                    <li>
                        <FunnelStageLegend label='Queued next product' />
                    </li>
                    <ArrowDashed
                        aria-hidden='true'
                        className='text-gray pointer-events-none absolute top-1/2 h-5 w-24 -translate-x-8 -translate-y-0.5 lg:left-60 xl:left-72'
                    />
                    <ArrowDashed
                        aria-hidden='true'
                        className='text-gray pointer-events-none absolute top-1/2 left-[32rem] h-5 w-24 -translate-x-8 -translate-y-0.5 lg:left-[31.5rem] xl:left-[38rem]'
                    />
                </ul>
            </div>

            <StanButton
                name='create-first-funnel'
                onClick={onCreateFunnel}
                icon={IcPlus}
                disabled={isLoading}
                className='w-full lg:w-auto'
            >
                {isLoading ? 'Creating...' : 'Create Your First Funnel'}
            </StanButton>
        </div>
    );
};
